import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UserContext } from "../../providers/UserProvider";
import { getEbookById, submitEbook, getOrderCheck } from "../../app/api";
import { useTranslation } from 'react-i18next';
import FloatingShapes from "../common/FloatingShapes";

const EbookPayment = () => {
  const { t } = useTranslation();
  const { userBio } = useContext(UserContext);
  const { id } = useParams();
  const [ebook, setEbook] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [orderResponse, setOrderResponse] = useState<any>(undefined);
  const [paymentStatus, setPaymentStatus] = useState<any>(undefined);
  const [orderId, setOrderId] = useState<any>(undefined);
  const [serverError, setServerError] = useState<any>(undefined);

  useEffect(() => {
    setLoading(true);
    getEbookById(id)
      .then((res) => {
        setEbook(res.data);
        if (res.data.purchased) {
          setPaymentStatus('Төлсөн');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [id]);

  const checkPayment = async () => {
    try {
      const resp = await getEbookById(id);
      if (resp.data.purchased) {
        setPaymentStatus('Төлсөн');
      } else {
        alert(t('ebookPayment.paymentPending'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = useCallback(async () => {
    setRequestLoading(true);
    setServerError(undefined);
    try {
      const resp = await submitEbook(id);
      setOrderId(resp.data.id || resp.data._id);
      setOrderResponse(resp.data.qpayData);
      setPaymentStatus(resp.data.paymentStatus);
    } catch (err: any) {
      if (err.response) {
        setServerError(err.response.data.error);
      } else {
        setServerError(t('payment.pleaseWait'));
      }
    }
    setRequestLoading(false);
  }, [id]);

  if (loading || !ebook) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      {/* Dark Hero Header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/15 blur-[100px]" />
        </div>
        <FloatingShapes variant="sparse" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-secondary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
              {t('ebookPayment.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              {t('ebookPayment.title')}
            </h1>
          </div>
        </div>
      </div>

      <div className="bg-neutral-50 py-16 text-neutral-800">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          {/* Ebook info card */}
          <div className="card p-6 mb-8 flex gap-6">
            {ebook.cover && (
              <img src={ebook.cover} alt={ebook.title} className="w-24 h-32 object-cover rounded-lg shadow-md flex-shrink-0" />
            )}
            <div>
              <h2 className="text-xl font-bold text-neutral-900">{ebook.title}</h2>
              <p className="text-neutral-500 mt-1">{ebook.author}</p>
              <p className="text-lg font-extrabold mt-2 bg-gradient-to-r from-primary-600 to-secondary-500 bg-clip-text text-transparent">
                ₮{ebook.price?.toLocaleString()}
              </p>
            </div>
          </div>

          {!orderResponse && paymentStatus !== 'Төлсөн' ? (
            <div className="flex flex-col items-center">
              {requestLoading ? (
                <button
                  disabled
                  className="px-8 py-3 rounded-2xl bg-neutral-200 text-neutral-500 font-bold cursor-not-allowed"
                >
                  {t('ebookPayment.ordering')}
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="px-8 py-3 rounded-full font-bold text-white bg-gradient-to-r from-primary-600 to-secondary-500 shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-secondary-500/30 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-secondary-300 transition-all duration-200"
                >
                  {t('ebookPayment.buyQPay')}
                </button>
              )}
            </div>
          ) : paymentStatus === 'Төлсөн' ? (
            <div className="card p-10 flex flex-col justify-center items-center text-center">
              <p className="text-lg font-semibold text-neutral-900 mb-4">{t('ebookPayment.paymentSuccess')}</p>
              <Link
                to={`/ebook/read/${id}`}
                className="btn-primary px-8 py-3"
              >
                {t('ebookPayment.goToRead')}
              </Link>
            </div>
          ) : (
            <div className="card p-10 flex flex-col justify-center text-center">
              <p className="text-neutral-700 mb-4">{t('ebookPayment.payQR')}</p>
              <img
                src={`data:image/png;base64, ${orderResponse.qr_image}`}
                className="block mx-auto max-w-full h-auto rounded-xl"
              />
              <a
                href={orderResponse.qPay_shortUrl}
                target="_blank"
                className="underline text-primary-600 hover:text-primary-700 font-medium mt-4 inline-block"
              >
                {t('ebookPayment.payApp')}
              </a>
              <div className="w-full flex justify-center mt-4">
                <button
                  onClick={checkPayment}
                  className="btn-secondary px-8 py-3"
                >
                  {t('ebookPayment.checkPayment')}
                </button>
              </div>
            </div>
          )}

          {serverError && (
            <div className="bg-danger-50 border border-danger-200 text-danger-700 px-6 py-4 rounded-2xl mt-6 text-center">
              <span className="font-semibold">{JSON.stringify(serverError)}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EbookPayment;
